import ConfirmPopover from '@/components/common/ConfirmPopover';
import TextFieldWrapper from '@/components/common/TextFieldWrapper';
import { CustomNodeProps } from '@/components/flow/NodeRenderer';
import { useApplication } from '@/hooks/useApplication';
import { useMetadata } from '@/hooks/useMetadata';
import { useSubroutine } from '@/hooks/useSubroutine';
import { ConfigurationEnum } from '@/types/metadata/metadata';
import { updateDetail } from '@/util/application';
import { adjustBrightness, setOpacity } from '@/util/color';
import { deleteBlock } from '@/util/delete';
import { NoteHelper } from '@/util/note';
import ClearIcon from '@mui/icons-material/Clear';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import HeightIcon from '@mui/icons-material/Height';
import { Card, CardHeader, Stack, useTheme } from '@mui/material';
import { NodeResizeControl, useReactFlow } from '@xyflow/react';
import { FC, useRef } from 'react';
import NodeSelection from './NodeSelection';
import ColorPicker, { Color } from './note/ColorPicker';

const CustomNote: FC<CustomNodeProps> = ({ id, data, ...props }) => {
  const { metadata } = useMetadata();
  const { application, setApplication } = useApplication();
  const { getZoom, updateNode } = useReactFlow();
  const { selectedSubroutineId } = useSubroutine();
  const theme = useTheme();
  const zoom = getZoom();

  const nodeRef = useRef<HTMLDivElement | null>(null);

  const bgColor = NoteHelper.getColor(application.blocks.details[id]?.color, theme.palette.mode === 'dark' ? 600 : 300);
  const textColor = theme.palette.getContrastText(bgColor);
  const contrastColor = theme.palette.mode === 'dark' ? 'white' : 'black';

  const getAvailableColors = (): Color[] => {
    const metaBlock = metadata.blocks.find((block) => block.type === data.backendNode.type);
    if (metaBlock) {
      const colorConfig = metaBlock.configuration.find((conf) => conf.value === 'color') as ConfigurationEnum;
      if (!colorConfig) return [];
      return colorConfig.options;
    }
    return [];
  };

  const availableColors = getAvailableColors();
  return (
    <NodeSelection {...props} id={id} data={data} zoom={zoom || 1}>
      {props.selected && (
        <NodeResizeControl
          minWidth={160}
          minHeight={160}
          style={{ background: 'transparent', border: 'none' }}
          position='right'
          onResizeEnd={(_, params) => setApplication(updateDetail(id, 'width', params.width, application))}
        >
          <HeightIcon
            style={{
              transform: 'rotate(90deg)',
              width: 3 - (zoom || 1) + 'em',
              height: 'auto',
              marginLeft: '15px',
              position: 'absolute',
              top: -10 / (zoom || 1),
              color: contrastColor
            }}
          />
        </NodeResizeControl>
      )}
      <Card
        ref={nodeRef}
        elevation={4}
        variant='elevation'
        sx={{
          minWidth: '10rem',
          minHeight: '5rem',
          width: application.blocks.details[id]?.width || 'inherit',
          bgcolor: bgColor,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0
        }}
      >
        <CardHeader
          className='custom-drag-handle'
          title={
            <Stack direction='row' justifyContent='space-between' alignItems='center' height={'1.5em'}>
              <Stack direction='row'>
                {Array.from({ length: 3 }).map((i, index) => {
                  return <DragIndicatorIcon key={`drag.${index}`} fontSize='small' sx={{ opacity: 0.5, mr: -1 }} />;
                })}
              </Stack>
              <Stack direction='row'>
                <ColorPicker
                  colors={availableColors}
                  onChange={(color) => setApplication(updateDetail(id, 'color', color, application))}
                  sx={{
                    color: textColor,
                    opacity: 0.5
                  }}
                />
                <ConfirmPopover
                  onConfirm={() => {
                    if (!selectedSubroutineId) return;

                    const updatedApplication = deleteBlock(
                      structuredClone(application),
                      selectedSubroutineId,
                      parseInt(id)
                    );
                    setApplication(updatedApplication);
                  }}
                  icon={<ClearIcon />}
                  text='Delete this note?'
                  color={textColor}
                  opacity={0.5}
                  size='small'
                />
              </Stack>
            </Stack>
          }
          sx={{
            p: 0.5,
            color: textColor,
            bgcolor: adjustBrightness(bgColor, 0.9)
          }}
        />
        <Stack p={1} gap={1} direction='row' alignItems='top'>
          <TextFieldWrapper
            placeholder='Write a note'
            fullWidth
            multiline
            minRows={4}
            value={application.blocks.details[id]?.note ?? ''}
            onClick={(e) => {
              e.stopPropagation();
              updateNode(id, { selected: false });
            }}
            onChange={(e) => {
              const value = e.currentTarget.value;
              if (!application.blocks.details[id]?.note && value === application.blocks.details[id].note) return;

              setApplication(updateDetail(id, 'note', value, application));
            }}
            sx={{
              bgcolor: bgColor,
              color: textColor,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: setOpacity(textColor, 0.25)
                },
                '&:hover fieldset': {
                  borderColor: setOpacity(textColor, 0.25)
                },
                '&.Mui-focused fieldset': {
                  borderColor: setOpacity(textColor, 0.25)
                }
              }
            }}
            inputProps={{
              style: {
                color: textColor
              }
            }}
          />
        </Stack>
      </Card>
    </NodeSelection>
  );
};

export default CustomNote;
